import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  MdHistory,
  MdFilterList,
  MdCalendarToday,
  MdClose,
  MdRefresh,
  MdVisibility,
  MdAccountBalanceWallet,
  MdErrorOutline,
} from "react-icons/md";
import { BASE_API_URL } from "../helpers/Config";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import moment from "moment";
import { getPageStyles, MOTION } from "../helpers/pageTheme";

const STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "0", label: "Pending" },
  { value: "1", label: "Completed" },
  { value: "2", label: "Rejected" },
];

const getStatusMeta = (status, isDark) => {
  const map = {
    "0": {
      text: "Pending",
      bg: isDark ? "rgba(245, 158, 11, 0.15)" : "#fffbeb",
      color: isDark ? "#fcd34d" : "#b45309",
      border: isDark ? "rgba(245, 158, 11, 0.35)" : "#fde68a",
    },
    "1": {
      text: "Completed",
      bg: isDark ? "rgba(34, 197, 94, 0.15)" : "#ecfdf5",
      color: isDark ? "#4ade80" : "#15803d",
      border: isDark ? "rgba(34, 197, 94, 0.35)" : "#a7f3d0",
    },
    "2": {
      text: "Rejected",
      bg: isDark ? "rgba(239, 68, 68, 0.15)" : "#fef2f2",
      color: isDark ? "#f87171" : "#b91c1c",
      border: isDark ? "rgba(239, 68, 68, 0.35)" : "#fecaca",
    },
  };
  return map[status] || {
    text: "Unknown",
    bg: isDark ? "#1f2937" : "#f3f4f6",
    color: isDark ? "#9ca3af" : "#6b7280",
    border: isDark ? "#374151" : "#e5e7eb",
  };
};

const SectionCard = ({ styles, children, className = "", style = {} }) => (
  <div className={`p-3 ${className}`} style={{ ...styles.card, ...style }}>
    {children}
  </div>
);

const WithdrawalHistory = ({ theme = "dark" }) => {
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastId, setLastId] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const navigate = useNavigate();
  const containerRef = useRef(null);
  const isInitialMount = useRef(true);
  const isFetching = useRef(false);

  const isDark = theme === "dark";
  const styles = useMemo(() => getPageStyles(isDark), [isDark]);

  const inputStyle = useMemo(
    () => ({
      ...styles.input,
      fontSize: "14px",
      height: "42px",
      paddingLeft: "40px",
      paddingRight: selectedDate ? "40px" : "12px",
      width: "100%",
      cursor: "pointer",
    }),
    [styles.input, selectedDate]
  );

  const fetchWithdrawals = async ({
    last_id = 0,
    isLoadingMore = false,
    date = "",
    status = "all",
  }) => {
    if (isFetching.current) return;

    try {
      isFetching.current = true;
      setLoading(true);
      setError(null);

      const response = await axios.post(`${BASE_API_URL}/payment/withdrawl-report`, {
        token: localStorage.getItem("a5srf4wer7w1cd"),
        last_id,
        date,
        status,
      });
      const data = response?.data;

      if (data?.error) {
        if (data?.error === "session expired") {
          localStorage.removeItem("a5srf4wer7w1cd");
          navigate("/login");
          return;
        }
        toast.error(data?.error);
        return;
      }

      if (data?.data) {
        setHasMore(data?.has_more);
        setLastId(data?.last_id);
        if (isLoadingMore) {
          setWithdrawals((prev) => [...prev, ...data.data]);
        } else {
          setWithdrawals(data.data);
        }
      }
    } catch (err) {
      console.log(err);
      toast.error("Failed to fetch withdrawals");
      setError("Failed to fetch withdrawal history. Please try again.");
    } finally {
      setLoading(false);
      isFetching.current = false;
    }
  };

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchWithdrawals({ last_id: 0, isLoadingMore: false, status: "all", date: "" });
    }
  }, []);

  useEffect(() => {
    if (!isInitialMount.current) {
      setWithdrawals([]);
      setLastId(0);
      setHasMore(true);
      fetchWithdrawals({
        last_id: 0,
        date: selectedDate || "",
        status: selectedStatus,
        isLoadingMore: false,
      });
    }
  }, [selectedStatus]);

  const handleScroll = useCallback(() => {
    if (!containerRef.current || loading || !hasMore || withdrawals.length === 0) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      fetchWithdrawals({
        last_id: lastId,
        status: selectedStatus,
        isLoadingMore: true,
        date: selectedDate || "",
      });
    }
  }, [hasMore, loading, lastId, withdrawals.length, selectedStatus, selectedDate]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let scrollTimeout;
    const throttledScrollHandler = () => {
      if (scrollTimeout) return;
      scrollTimeout = setTimeout(() => {
        handleScroll();
        scrollTimeout = null;
      }, 100);
    };

    container.addEventListener("scroll", throttledScrollHandler);
    return () => {
      container.removeEventListener("scroll", throttledScrollHandler);
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, [handleScroll]);

  const filteredWithdrawals = withdrawals.filter(
    (item) => selectedStatus === "all" || item.status === selectedStatus
  );

  const formatDate = (dateString) =>
    moment(dateString, "YYYY-MM-DD HH:mm:ss").format("DD MMM YYYY, hh:mm A");

  const formatAmount = (amount) => {
    const numericAmount = parseFloat(amount);
    if (isNaN(numericAmount)) return "$0.00";
    return `$${numericAmount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatWalletType = (walletType) => {
    if (!walletType) return "Unknown";
    return walletType
      .replace("usdt-", "USDT ")
      .replace("bep20", "BEP20")
      .replace("erc20", "ERC20")
      .replace("trc20", "TRC20")
      .toUpperCase();
  };

  const handleShowDetails = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedWithdrawal(null);
  };

  const handleResetDate = () => {
    setSelectedDate(null);
    fetchWithdrawals({
      last_id: 0,
      date: "",
      status: selectedStatus,
      isLoadingMore: false,
    });
  };

  const handleDateChange = (date) => {
    if (!date) {
      handleResetDate();
      return;
    }
    const formatted = moment(date).format("YYYY-MM-DD");
    setSelectedDate(formatted);
    setWithdrawals([]);
    setLastId(0);
    setHasMore(true);
    fetchWithdrawals({
      last_id: 0,
      date: formatted,
      status: selectedStatus,
      isLoadingMore: false,
    });
  };

  const setTodayFilter = () => {
    const today = moment().format("YYYY-MM-DD");
    setSelectedDate(today);
    setWithdrawals([]);
    setLastId(0);
    setHasMore(true);
    fetchWithdrawals({
      last_id: 0,
      date: today,
      status: selectedStatus,
      isLoadingMore: false,
    });
  };

  const handleRetry = () => {
    fetchWithdrawals({
      last_id: 0,
      isLoadingMore: false,
      date: selectedDate || "",
      status: selectedStatus,
    });
  };

  const DateInput = React.forwardRef(({ value, onClick }, ref) => (
    <div className="position-relative w-100">
      <MdCalendarToday
        size={18}
        className="position-absolute"
        style={{
          left: 12,
          top: "50%",
          transform: "translateY(-50%)",
          color: "#6366f1",
          pointerEvents: "none",
          zIndex: 1,
        }}
      />
      <input
        type="text"
        className="form-control wh-datepicker-input"
        value={value}
        onClick={onClick}
        onChange={() => { }}
        ref={ref}
        readOnly
        placeholder="Pick a date"
        style={inputStyle}
        disabled={loading}
      />
    </div>
  ));
  DateInput.displayName = "DateInput";

  const selectedDateObj = selectedDate ? moment(selectedDate, "YYYY-MM-DD").toDate() : null;

  const dateLabel = selectedDate
    ? moment(selectedDate, "YYYY-MM-DD").format("DD MMM YYYY")
    : "Any date";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35 }}
      className={`withdrawal-history-page ${isDark ? "wh-dark" : "wh-light"}`}
      style={styles.page}
    >
      <motion.div
        variants={MOTION.container}
        initial="hidden"
        animate="visible"
        className="d-flex flex-column gap-3 px-3 py-3"
        style={{ maxWidth: 480, margin: "0 auto" }}
      >
        {/* Hero */}
        <motion.div variants={MOTION.item} style={styles.hero()}>
          <div className="d-flex align-items-center gap-2 mb-1">
            <MdHistory size={26} />
            <div>
              <h5 className="fw-bold mb-0">Withdrawal History</h5>
              <p className="mb-0 small opacity-75">Track payouts & status</p>
            </div>
          </div>
          <div
            className="mt-3 py-2 px-3 rounded-3 d-flex justify-content-between align-items-center"
            style={{ background: "rgba(255,255,255,0.12)" }}
          >
            <span className="small opacity-75">Showing</span>
            <span className="fw-semibold small">{dateLabel}</span>
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div variants={MOTION.item}>
          <SectionCard styles={styles} style={{ overflow: "visible" }}>
            <p className="mb-2 d-flex align-items-center gap-1" style={styles.sectionTitle}>
              <MdFilterList size={16} />
              Status
            </p>
            <div className="d-flex flex-wrap gap-2 mb-3">
              {STATUS_FILTERS.map(({ value, label }) => (
                <motion.button
                  key={value}
                  type="button"
                  className="btn btn-sm"
                  style={styles.filterChip(selectedStatus === value)}
                  disabled={loading}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => setSelectedStatus(value)}
                >
                  {label}
                </motion.button>
              ))}
            </div>

            <p className="mb-2" style={styles.sectionTitle}>
              Date
            </p>
            <div className="position-relative mb-2">
              <DatePicker
                selected={selectedDateObj}
                onChange={handleDateChange}
                customInput={<DateInput />}
                dateFormat="dd/MM/yyyy"
                maxDate={new Date()}
                showPopperArrow={false}
                portalId="wh-datepicker-portal"
                popperClassName="wh-datepicker-popper"
                calendarClassName="wh-datepicker-calendar"
                disabled={loading}
                placeholderText="Pick a date"
                isClearable={false}
              />
              {selectedDate && (
                <motion.button
                  type="button"
                  className="btn btn-sm position-absolute d-flex align-items-center justify-content-center border-0"
                  style={{
                    right: 6,
                    top: "50%",
                    transform: "translateY(-50%)",
                    width: 30,
                    height: 30,
                    borderRadius: "8px",
                    background: isDark ? "#374151" : "#e5e7eb",
                    color: isDark ? "#f9fafb" : "#374151",
                    zIndex: 2,
                  }}
                  whileTap={{ scale: 0.92 }}
                  onClick={handleResetDate}
                  disabled={loading}
                  title="Clear date"
                >
                  <MdClose size={16} />
                </motion.button>
              )}
            </div>
            <div className="d-flex flex-wrap gap-2">
              <motion.button
                type="button"
                className="btn btn-sm"
                style={styles.filterChip(false)}
                whileTap={{ scale: 0.97 }}
                onClick={setTodayFilter}
                disabled={loading}
              >
                Today
              </motion.button>
              {selectedDate && (
                <motion.button
                  type="button"
                  className="btn btn-sm"
                  style={styles.outlineBtn}
                  whileTap={{ scale: 0.97 }}
                  onClick={handleResetDate}
                  disabled={loading}
                >
                  Clear date
                </motion.button>
              )}
            </div>
          </SectionCard>
        </motion.div>

        {/* Error */}
        {error && (
          <motion.div variants={MOTION.item}>
            <SectionCard styles={styles}>
              <div className="text-center py-4">
                <MdErrorOutline size={40} className="mb-2 text-danger" />
                <p className="mb-3" style={{ color: styles.textMuted }}>
                  {error}
                </p>
                <motion.button
                  type="button"
                  className="btn"
                  style={styles.submitBtn}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleRetry}
                >
                  <MdRefresh className="me-1" size={18} />
                  Try again
                </motion.button>
              </div>
            </SectionCard>
          </motion.div>
        )}

        {/* List — flat layout, no card */}
        <motion.div variants={MOTION.item}>
          <p className="mb-2 px-1" style={styles.sectionTitle}>
            {filteredWithdrawals.length} record
            {filteredWithdrawals.length !== 1 ? "s" : ""}
          </p>
          <div
            ref={containerRef}
            className="wh-list-scroll overflow-auto"
            style={{
              maxHeight: "calc(100vh - 340px)",
              minHeight: 200,
            }}
          >
            {!error && filteredWithdrawals.length === 0 && !loading ? (
              <div style={styles.empty}>
                <MdAccountBalanceWallet
                  size={48}
                  className="mb-3 opacity-50"
                />
                <h6 className="fw-semibold mb-1">No withdrawals found</h6>
                <p className="mb-0 small">
                  Try another status or date filter
                </p>
              </div>
            ) : (
              <AnimatePresence mode="popLayout">
                {filteredWithdrawals.map((withdrawal, index) => {
                  const meta = getStatusMeta(withdrawal.status, isDark);
                  return (
                    <motion.div
                      key={withdrawal.withdrawl_id}
                      layout
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2, delay: index * 0.02 }}
                      className="py-3"
                      style={{
                        borderBottom:
                          index < filteredWithdrawals.length - 1
                            ? `1px solid ${styles.listBorder}`
                            : "none",
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        <div className="flex-grow-1 min-w-0">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <span
                              className="fw-bold"
                              style={{ fontSize: "0.85rem" }}
                            >
                              #{withdrawal.id}
                            </span>
                            <span
                              className="badge"
                              style={{
                                backgroundColor: meta.bg,
                                color: meta.color,
                                border: `1px solid ${meta.border}`,
                                fontSize: "0.65rem",
                                fontWeight: 700,
                              }}
                            >
                              {meta.text}
                            </span>
                          </div>
                          <p
                            className="mb-1 small"
                            style={{ color: styles.textMuted }}
                          >
                            {formatDate(withdrawal.date)}
                          </p>
                          <p className="mb-0 small">
                            {withdrawal.type === "bank"
                              ? "Bank transfer"
                              : "Crypto"}
                            {withdrawal.type === "crypto" &&
                              withdrawal.wallet_type && (
                                <span
                                  className="ms-1 badge"
                                  style={{
                                    backgroundColor: isDark
                                      ? "#1e293b"
                                      : "#f1f5f9",
                                    color: styles.textMuted,
                                    fontSize: "0.6rem",
                                  }}
                                >
                                  {formatWalletType(
                                    withdrawal.wallet_type
                                  )}
                                </span>
                              )}
                          </p>
                          {withdrawal.charge > 0 && (
                            <p
                              className="mb-0 mt-1 small"
                              style={{ color: styles.textMuted }}
                            >
                              Fee {formatAmount(withdrawal.charge)}{" "}
                              · Net {formatAmount(withdrawal.net)}
                            </p>
                          )}
                        </div>
                        <div className="text-end flex-shrink-0">
                          <div
                            className="fw-bold mb-2"
                            style={{
                              fontSize: "1rem",
                              color: "#6366f1",
                            }}
                          >
                            {formatAmount(withdrawal.amount)}
                          </div>
                          <motion.button
                            type="button"
                            className="btn btn-sm d-inline-flex align-items-center gap-1"
                            style={{
                              ...styles.outlineBtn,
                              fontSize: "0.72rem",
                              padding: "4px 10px",
                            }}
                            whileTap={{ scale: 0.96 }}
                            onClick={() =>
                              handleShowDetails(withdrawal)
                            }
                          >
                            <MdVisibility size={14} />
                            Details
                          </motion.button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            )}

            {loading && (
              <div className="text-center py-4">
                <div
                  className="spinner-border spinner-border-sm text-primary mb-2"
                  role="status"
                />
                <p
                  className="mb-0 small"
                  style={{ color: styles.textMuted }}
                >
                  {withdrawals.length === 0
                    ? "Loading history…"
                    : "Loading more…"}
                </p>
              </div>
            )}

            {!hasMore && filteredWithdrawals.length > 0 && !loading && (
              <p
                className="text-center py-3 mb-0 small"
                style={{ color: styles.textMuted }}
              >
                End of list
              </p>
            )}
          </div>
        </motion.div>
      </motion.div>

      {createPortal(
        <AnimatePresence>
          {showModal && selectedWithdrawal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="wh-modal-backdrop"
              onClick={handleCloseModal}
            >
              <motion.div
                initial={{ scale: 0.92, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.92, opacity: 0 }}
                className="wh-modal-panel"
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  className="border-0"
                  style={{ ...styles.modal, width: "100%" }}
                >
                  <div className="modal-header border-0 px-4 pt-4 pb-0">
                    <h6 className="modal-title fw-bold">Withdrawal details</h6>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={handleCloseModal}
                      aria-label="Close"
                    />
                  </div>
                  <div className="modal-body px-4 py-3">
                    {[
                      ["Withdrawal ID", selectedWithdrawal.id],
                      ["Transaction ID", selectedWithdrawal.withdrawl_id],
                      ["Date", formatDate(selectedWithdrawal.date)],
                      ["Amount", formatAmount(selectedWithdrawal.amount)],
                      [
                        "Processing fee",
                        `-${formatAmount(selectedWithdrawal.charge)}`,
                      ],
                      ["Net amount", formatAmount(selectedWithdrawal.net)],
                      ["Type", selectedWithdrawal.type],
                    ].map(([label, value]) => (
                      <div
                        key={label}
                        className="d-flex justify-content-between py-2"
                        style={{
                          borderBottom: `1px solid ${styles.listBorder}`,
                          fontSize: "0.85rem",
                        }}
                      >
                        <span style={{ color: styles.textMuted }}>{label}</span>
                        <span
                          className="fw-semibold text-end"
                          style={{
                            maxWidth: "58%",
                            wordBreak: "break-word",
                          }}
                        >
                          {value}
                        </span>
                      </div>
                    ))}
                    <div className="d-flex justify-content-between py-2 mb-2">
                      <span style={{ color: styles.textMuted }}>Status</span>
                      {(() => {
                        const sm = getStatusMeta(
                          selectedWithdrawal.status,
                          isDark
                        );
                        return (
                          <span
                            className="badge"
                            style={{
                              backgroundColor: sm.bg,
                              color: sm.color,
                              border: `1px solid ${sm.border}`,
                              fontWeight: 700,
                            }}
                          >
                            {sm.text}
                          </span>
                        );
                      })()}
                    </div>

                    {selectedWithdrawal.type === "bank" && (
                      <div
                        className="p-3 rounded mb-2"
                        style={{
                          backgroundColor: isDark ? "#0f172a" : "#f8fafc",
                          border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
                        }}
                      >
                        <p className="fw-bold small mb-2">Bank details</p>
                        {[
                          ["Bank", selectedWithdrawal.bank_name],
                          ["Account", selectedWithdrawal.account_no],
                          ["IFSC", selectedWithdrawal.ifsc_code],
                          ["Holder", selectedWithdrawal.holder],
                        ].map(([l, v]) => (
                          <div
                            key={l}
                            className="d-flex justify-content-between small py-1"
                          >
                            <span style={{ color: styles.textMuted }}>
                              {l}
                            </span>
                            <span>{v || "N/A"}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {selectedWithdrawal.type === "crypto" && (
                      <div
                        className="p-3 rounded mb-2"
                        style={{
                          backgroundColor: isDark ? "#0f172a" : "#f8fafc",
                          border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
                        }}
                      >
                        <p className="fw-bold small mb-2">Crypto details</p>
                        <div className="d-flex justify-content-between small py-1">
                          <span style={{ color: styles.textMuted }}>
                            Network
                          </span>
                          <span>
                            {formatWalletType(
                              selectedWithdrawal.wallet_type
                            )}
                          </span>
                        </div>
                        <div className="small py-1">
                          <span
                            className="d-block mb-1"
                            style={{ color: styles.textMuted }}
                          >
                            Wallet address
                          </span>
                          <code
                            className="d-block"
                            style={{
                              fontSize: "0.72rem",
                              wordBreak: "break-all",
                            }}
                          >
                            {selectedWithdrawal.wallet_address}
                          </code>
                        </div>
                      </div>
                    )}

                    {selectedWithdrawal.status === "2" &&
                      selectedWithdrawal.reject_reason && (
                        <div
                          className="p-3 rounded"
                          style={{
                            backgroundColor: isDark
                              ? "rgba(239, 68, 68, 0.12)"
                              : "#fef2f2",
                            border: `1px solid ${isDark ? "#7f1d1d" : "#fecaca"
                              }`,
                            fontSize: "0.8rem",
                            color: isDark ? "#fca5a5" : "#b91c1c",
                          }}
                        >
                          <strong>Reject reason:</strong>{" "}
                          {selectedWithdrawal.reject_reason}
                        </div>
                      )}
                  </div>
                  <div className="modal-footer border-0 px-4 pb-4">
                    <button
                      type="button"
                      className="btn w-100"
                      style={styles.submitBtn}
                      onClick={handleCloseModal}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}

      <style>{`
                .withdrawal-history-page {
                    overflow-x: hidden;
                }

                .wh-list-scroll {
                    scrollbar-width: none;
                    -ms-overflow-style: none;
                }
                .wh-list-scroll::-webkit-scrollbar {
                    display: none;
                    width: 0;
                    height: 0;
                }

                .wh-modal-backdrop {
                    position: fixed;
                    inset: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 16px;
                    background-color: rgba(0, 0, 0, 0.55);
                    backdrop-filter: blur(4px);
                    z-index: 10000;
                }
                .wh-modal-panel {
                    width: 100%;
                    max-width: 400px;
                    max-height: 85vh;
                    overflow-y: auto;
                    margin: auto;
                }
                .wh-modal-panel::-webkit-scrollbar {
                    display: none;
                }
                .wh-modal-panel {
                    scrollbar-width: none;
                    -ms-overflow-style: none;
                }

                .withdrawal-history-page .react-datepicker-wrapper {
                    width: 100%;
                }

                #wh-datepicker-portal {
                    position: relative;
                    z-index: 2147483647 !important;
                    pointer-events: none;
                }
                #wh-datepicker-portal > * {
                    pointer-events: auto;
                }
                #wh-datepicker-portal .react-datepicker-popper,
                .react-datepicker-popper.wh-datepicker-popper {
                    z-index: 2147483647 !important;
                }
                .wh-datepicker-popper .react-datepicker {
                    font-family: inherit;
                    border-radius: 14px;
                    border: 1px solid ${isDark ? "#1f2937" : "#e5e7eb"};
                    box-shadow: 0 16px 40px rgba(0,0,0,${isDark ? "0.45" : "0.12"});
                    overflow: hidden;
                }
                .wh-dark .wh-datepicker-popper .react-datepicker {
                    background: #020617;
                    color: #f9fafb;
                }
                .wh-light .wh-datepicker-popper .react-datepicker {
                    background: #ffffff;
                    color: #111827;
                }
                .wh-datepicker-popper .react-datepicker__header {
                    background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
                    border-bottom: none;
                    padding-top: 12px;
                }
                .wh-datepicker-popper .react-datepicker__current-month,
                .wh-datepicker-popper .react-datepicker__day-name,
                .wh-datepicker-popper .react-datepicker-time__header {
                    color: #fff;
                    font-weight: 600;
                }
                .wh-datepicker-popper .react-datepicker__day-name {
                    opacity: 0.9;
                    font-size: 0.75rem;
                }
                .wh-datepicker-popper .react-datepicker__navigation-icon::before {
                    border-color: #fff;
                }
                .wh-datepicker-popper .react-datepicker__navigation:hover *::before {
                    border-color: rgba(255,255,255,0.85);
                }
                .wh-dark .wh-datepicker-popper .react-datepicker__day {
                    color: #e5e7eb;
                }
                .wh-light .wh-datepicker-popper .react-datepicker__day {
                    color: #374151;
                }
                .wh-datepicker-popper .react-datepicker__day:hover {
                    background: ${isDark ? "#1e293b" : "#eef2ff"};
                    border-radius: 8px;
                }
                .wh-datepicker-popper .react-datepicker__day--selected,
                .wh-datepicker-popper .react-datepicker__day--keyboard-selected {
                    background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%) !important;
                    color: #fff !important;
                    border-radius: 8px;
                    font-weight: 600;
                }
                .wh-datepicker-popper .react-datepicker__day--today {
                    font-weight: 700;
                    color: #6366f1;
                }
                .wh-datepicker-popper .react-datepicker__day--disabled {
                    opacity: 0.35;
                }
                .wh-datepicker-popper .react-datepicker__month-dropdown,
                .wh-datepicker-popper .react-datepicker__year-dropdown {
                    background: ${isDark ? "#0f172a" : "#fff"};
                    border: 1px solid ${isDark ? "#334155" : "#e5e7eb"};
                    border-radius: 8px;
                }
                .wh-datepicker-popper .react-datepicker__triangle {
                    display: none;
                }
            `}</style>
    </motion.div>
  );
};

export default WithdrawalHistory;
